/**
 * Debt-to-income for programs whose rate grid is graded by DTI (FHA, VA,
 * USDA). Uses the borrower's profile income and existing monthly debts plus
 * this home's housing payment (back-end DTI).
 *
 * DSCR, commercial and other investor programs qualify on the property's
 * cash flow, not the borrower's DTI — never pass this to them.
 */
export type BorrowerDebtProfile = {
  monthlyIncome?: number | null;
  monthlyDebt?: number | null;
};

export function monthlyPrincipalAndInterest(
  principal: number,
  ratePercent: number,
  termYears: number
): number {
  const n = termYears * 12;
  const r = ratePercent / 12 / 100;
  if (!(principal > 0) || !(n > 0)) return 0;
  if (!(r > 0)) return principal / n;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

/** Back-end DTI as a percent, or null when the profile has no income. */
export function backEndDtiPercent(
  profile: BorrowerDebtProfile | undefined,
  housingPayment: number
): number | null {
  const income = Number(profile?.monthlyIncome);
  if (!Number.isFinite(income) || income <= 0) return null;
  const debt = Math.max(0, Number(profile?.monthlyDebt) || 0);
  return Math.round(((debt + housingPayment) / income) * 1000) / 10;
}

/**
 * Re-prices a DTI-graded program with the borrower's DTI on this home.
 * The housing payment is taken at the grid-default rate, which is close
 * enough to pick the DTI bucket. Without income the rate is unchanged
 * (the grid's default DTI applies).
 */
export function priceWithProfileDti(
  rate: number,
  opts: {
    profile?: BorrowerDebtProfile;
    loanAmount: number;
    loanTerm: number;
    /** Taxes, insurance, mortgage insurance/guarantee fee, HOA. */
    otherHousing: number;
    reprice?: (dti: number) => number;
  }
): { rate: number; dti: number | null } {
  const housing =
    monthlyPrincipalAndInterest(opts.loanAmount, rate, opts.loanTerm) +
    opts.otherHousing;
  const dti = backEndDtiPercent(opts.profile, housing);
  if (dti == null || !opts.reprice) return { rate, dti };
  const repriced = opts.reprice(dti);
  return { rate: Number.isFinite(repriced) ? repriced : rate, dti };
}
